import { AxiosInstance } from '@/api';
import { AUTH_KEY, FARM_DAY, FARM_DAY_KEY } from '@/constants';
import { LoginResponse, LoginResponseUser } from '@/query';
import type { ReactNode } from 'react';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';

import { createContext as createContextSelector, useContextSelector } from 'use-context-selector';

export enum Sessions {
  admin = 'Painel',
  reports = 'Relatórios',
  management = 'Manejo',
  tract = 'Trato',
  diet = 'Formulação',
  financial = 'Financeiro',
  traceability = 'Rastreabilidade',
  farm = 'Fazenda',
  medicalServices = 'Sanidade',
  logout = 'Sair',
  batches = 'Lotes avulsos'
}

type ContextData = {
  user: LoginResponseUser;
  token: string;
  refresh: string;
  farmDay: Date;
  farmDayUTC: string;
  openMenu: boolean;
  currentSession: Sessions | null;
  currentNavigation: string;

  handleOpenMenu: () => void;
  handleCurrentSession: (session: Sessions) => void;
  handleCurrentNavigation: (navigation: string) => void;
  handleFarmDay: (date: Date) => void;
};

type ProviderProps = {
  children: ReactNode;
};

export const CommonLayoutContext = createContextSelector({} as ContextData);

export function CommonLayoutProvider(props: ProviderProps) {
  const navigate = useNavigate();

  const openMenuLocal = localStorage.getItem('FEED:open-menu');
  const currentSessionLocal = localStorage.getItem('FEED:current-session');
  const currentNavigationLocal = localStorage.getItem('FEED:current-navigation');

  const auth: LoginResponse = JSON.parse(localStorage.getItem(AUTH_KEY) as any);

  const user: LoginResponseUser = auth.user;
  const token = auth.token;
  const refresh = auth.refresh;

  const farmDayUTC = localStorage.getItem(FARM_DAY_KEY) as string;
  const day = new Date(farmDayUTC);
  const [farmDay, setFarmDay] = useState<Date>(day);

  const [openMenu, setOpenMenu] = useState<boolean>(openMenuLocal ? JSON.parse(openMenuLocal) : false);
  const [currentSession, setCurrentSession] = useState<Sessions | null>(
    currentSessionLocal ? JSON.parse(currentSessionLocal) : null
  );
  const currentNavigation = currentNavigationLocal ? JSON.parse(currentNavigationLocal) : '';

  const handleOpenMenu = useCallback(() => {
    setOpenMenu((prevState) => {
      localStorage.setItem('FEED:open-menu', JSON.stringify(!prevState));

      return !prevState;
    });
  }, []);

  const handleCurrentSession = useCallback(
    (session: Sessions) => {
      // localStorage.setItem('FEED:current-session', JSON.stringify(session));
      setCurrentSession(session);
    },
    [currentSession]
  );

  const handleCurrentNavigation = useCallback((navigation: string) => {
    if (navigation === '') return;

    localStorage.setItem('FEED:current-navigation', JSON.stringify(navigation));

    navigate(navigation);
  }, []);

  const handleFarmDay = useCallback(
    (date: Date) => {
      localStorage.setItem(FARM_DAY_KEY, date.toISOString());

      AxiosInstance.defaults.headers.common[FARM_DAY] = date.toISOString();

      setFarmDay(date);
    },
    [farmDay]
  );

  return (
    <CommonLayoutContext.Provider
      value={{
        user,
        token,
        refresh,
        farmDay,
        farmDayUTC,
        openMenu,
        currentSession,
        currentNavigation,

        handleOpenMenu,
        handleCurrentSession,
        handleCurrentNavigation,
        handleFarmDay
      }}
    >
      {props.children}
    </CommonLayoutContext.Provider>
  );
}

export function useCommonLayout() {
  const user = useContextSelector(CommonLayoutContext, (context) => context.user);
  const token = useContextSelector(CommonLayoutContext, (context) => context.token);
  const refresh = useContextSelector(CommonLayoutContext, (context) => context.refresh);
  const farmDay = useContextSelector(CommonLayoutContext, (context) => context.farmDay);
  const farmDayUTC = useContextSelector(CommonLayoutContext, (context) => context.farmDayUTC);
  const openMenu = useContextSelector(CommonLayoutContext, (context) => context.openMenu);
  const currentSession = useContextSelector(CommonLayoutContext, (context) => context.currentSession);
  const currentNavigation = useContextSelector(CommonLayoutContext, (context) => context.currentNavigation);

  const handleOpenMenu = useContextSelector(CommonLayoutContext, (context) => context.handleOpenMenu);
  const handleCurrentSession = useContextSelector(CommonLayoutContext, (context) => context.handleCurrentSession);
  const handleCurrentNavigation = useContextSelector(CommonLayoutContext, (context) => context.handleCurrentNavigation);
  const handleFarmDay = useContextSelector(CommonLayoutContext, (context) => context.handleFarmDay);

  return {
    user,
    token,
    refresh,
    farmDay,
    farmDayUTC,
    openMenu,
    currentSession,
    Sessions,
    currentNavigation,

    handleOpenMenu,
    handleCurrentSession,
    handleCurrentNavigation,
    handleFarmDay
  };
}
